import type { ApprovalTaskConfigFlow } from '../ApprovalTaskConfig';

/**
 * flow 内の承認段階ノードへの参照（段階順）
 */
export interface ApprovalStageNodeRef {
  /** flow ノードの id（保存済みであればエンジン Node の ID） */
  nodeId: string;
  /** 1 始まりの段階番号 */
  stageNumber: number;
  /** BE に保存済みのノードか（false の間は nodeId を要する設定を行えない） */
  isPersisted: boolean;
}

/**
 * flow から承認段階ノード（person / peopleGroup）を段階順に列挙する。
 *
 * 通知設定など「承認段階 = エンジン Node」単位の設定 UI が、
 * 各段階の nodeId と保存状態を参照するために使用する。
 */
export function listApprovalStageNodes(flow: ApprovalTaskConfigFlow): ApprovalStageNodeRef[] {
  const stages: ApprovalStageNodeRef[] = [];
  function collect(node: ApprovalTaskConfigFlow): void {
    if (node.type === 'person' || node.type === 'peopleGroup') {
      stages.push({
        nodeId: node.id,
        stageNumber: stages.length + 1,
        isPersisted: node.isPersisted === true
      });
    }
    for (const child of node.children ?? []) {
      collect(child);
    }
  }
  collect(flow);
  return stages;
}

/**
 * 既存のflowから承認段階数を取得する
 */
export function getStageCountFromFlow(flow: ApprovalTaskConfigFlow): number {
  let count = 0;
  function countApprovalNodes(node: ApprovalTaskConfigFlow): void {
    if (node.type === 'person' || node.type === 'peopleGroup') {
      count++;
    }
    if (node.children) {
      for (const child of node.children) {
        countApprovalNodes(child);
      }
    }
  }
  countApprovalNodes(flow);
  return count;
}

/**
 * 既存のflowを新しい段階数に合わせて調整する
 */
export function adjustFlowToStageCount(currentFlow: ApprovalTaskConfigFlow, newStageCount: number): ApprovalTaskConfigFlow {
  const currentStageCount = getStageCountFromFlow(currentFlow);
  if (currentStageCount === newStageCount) {
    return currentFlow;
  }

  // 既存の承認者ノードを収集
  const existingApprovers: ApprovalTaskConfigFlow[] = [];
  function collectApprovers(node: ApprovalTaskConfigFlow): void {
    if (node.type === 'person' || node.type === 'peopleGroup') {
      existingApprovers.push({
        ...node,
        children: undefined // 子ノードはリセット
      });
    }
    if (node.children) {
      for (const child of node.children) {
        collectApprovers(child);
      }
    }
  }
  collectApprovers(currentFlow);

  // 新しい段階数に合わせて承認者ノードを調整
  const adjustedApprovers: ApprovalTaskConfigFlow[] = [];
  for (let i = 0; i < newStageCount; i++) {
    if (i < existingApprovers.length) {
      // 既存の承認者を使用
      adjustedApprovers.push(existingApprovers[i]);
    } else {
      // 新しい承認者を追加（People Groupとして、中身は空）
      adjustedApprovers.push({
        id: `approver-${i + 1}`,
        type: 'peopleGroup'
        // peopleGroupプロパティを未設定にする
      });
    }
  }

  // 最後のノードにendノードを追加
  const lastApprover = adjustedApprovers[adjustedApprovers.length - 1];
  if (lastApprover) {
    lastApprover.children = [{
      id: 'end',
      type: 'end'
    }];
  }

  // ノードを連鎖させる
  for (let i = 0; i < adjustedApprovers.length - 1; i++) {
    const currentNode = adjustedApprovers[i];
    const nextNode = adjustedApprovers[i + 1];
    if (currentNode && nextNode) {
      currentNode.children = [nextNode];
    }
  }

  // startノードを作成
  const startNode: ApprovalTaskConfigFlow = {
    id: 'start',
    type: 'start',
    children: adjustedApprovers.length > 0 ? [adjustedApprovers[0]] : [{
      id: 'end',
      type: 'end'
    }]
  };
  return startNode;
}